import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type DecisionQuestionView, type DecisionSheetView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { Dialog } from '../../../components/ui/dialog';
import { errorMessage } from '../../../lib/error-message';
import { orgTimezone } from '../../../lib/org-timezone';
import { cn } from '../../../lib/utils';
import { useCurrentUserId } from '../../auth/auth-context';
import { raidApi, raidKeys } from '../../raid/api';
import { decisionSheetKeys } from '../api';
import { decisionSections, optionTitle } from '../model';
import {
  defaultRaidSelection,
  isRecordable,
  raidDecisionLabels,
  type RaidRecordOutcome,
  raidRecordState,
  recordDecisionsToRaid,
} from '../raid-record';

const PILL = 'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium';

/**
 * "Ghi vào RAID" — APM-DSHEET-f, plan §6.2. Lists the **decided** questions,
 * each with a checkbox; ticked by default when not in RAID yet or when the
 * answer changed after it was recorded ("đã đổi sau khi ghi").
 *
 * The RAID codes (`D-6`) come from the project's DECISION log — the sheet only
 * stores the item id. They label "đã ghi D-6" (a link to the RAID tab when the
 * page provides `onOpenRaid`) and feed "Thay quyết định D-6" when a changed
 * answer is recorded again.
 *
 * A partial failure keeps the dialog open: the recorded questions are unticked
 * (the refetched sheet shows them as "đã ghi") and the failed ones are listed
 * with their reason.
 */
export function RaidRecordDialog({
  view,
  onClose,
  onOpenRaid,
}: {
  readonly view: DecisionSheetView;
  readonly onClose: () => void;
  /** Go to the project's RAID tab. */
  readonly onOpenRaid?: (() => void) | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const decided = useMemo(
    () => decisionSections(view, 'answered').flatMap((section) => section.questions),
    [view],
  );
  const [picked, setPicked] = useState<Set<string>>(() => defaultRaidSelection(decided));
  const [outcome, setOutcome] = useState<RaidRecordOutcome | null>(null);

  const needsCodes = decided.some((question) => question.raidItemId !== null);
  const log = useQuery({
    queryKey: raidKeys.log(view.projectId, 'DECISION'),
    queryFn: () => raidApi.log(view.projectId, 'DECISION'),
    enabled: needsCodes,
  });
  const codes = useMemo(
    () => new Map((log.data?.items ?? []).map((item) => [item.id, item.code] as const)),
    [log.data],
  );

  // Owner of an outsider's answer (APM-DSX-b, plan §11.3).
  const recorderId = useCurrentUserId();
  const chosen = decided.filter((question) => picked.has(question.id) && isRecordable(question));

  const record = useMutation({
    mutationFn: () =>
      recordDecisionsToRaid(view, chosen, codes, {
        labels: raidDecisionLabels(t),
        timezone: orgTimezone(),
        recorderId,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['decision-sheet', view.id] });
      void queryClient.invalidateQueries({ queryKey: decisionSheetKeys.list(view.projectId) });
      void queryClient.invalidateQueries({ queryKey: ['raid', view.projectId] });
      if (result.failed.length === 0 && result.syncError === null) {
        toast.success(t('decisionSheet.raid.done', { count: result.recorded.length }));
        onClose();
        return;
      }
      setPicked((previous) => {
        const next = new Set(previous);
        for (const { question } of result.recorded) next.delete(question.id);
        return next;
      });
      setOutcome(result);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const toggle = (questionId: string): void =>
    setPicked((previous) => {
      const next = new Set(previous);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });

  const raidRef = (question: DecisionQuestionView): ReactNode => {
    const code = question.raidItemId ? codes.get(question.raidItemId) : undefined;
    if (!code) return null;
    return onOpenRaid ? (
      <button
        type="button"
        className="font-mono font-medium text-primary underline-offset-2 hover:underline"
        title={t('decisionSheet.raid.openRaid', { code })}
        onClick={onOpenRaid}
      >
        {code}
      </button>
    ) : (
      <span className="font-mono">{code}</span>
    );
  };

  return (
    <Dialog label={t('decisionSheet.raid.title')} size="lg" onClose={onClose}>
      <h2 className="text-lg font-semibold">{t('decisionSheet.raid.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('decisionSheet.raid.hint')}</p>

      {outcome ? (
        <div
          role="alert"
          className="flex flex-col gap-1 rounded-DEFAULT border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
        >
          {outcome.failed.length > 0 ? (
            <>
              <p>
                {t('decisionSheet.raid.partial', {
                  done: outcome.recorded.length,
                  failed: outcome.failed.length,
                })}
              </p>
              <ul className="list-disc pl-5">
                {outcome.failed.map(({ question, error }) => (
                  <li key={question.id} data-testid="decision-raid-failed">
                    <span className="font-mono">{question.code}</span> — {errorMessage(error, t)}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {outcome.syncError !== null ? (
            <p>
              {t('decisionSheet.raid.syncFailed', { reason: errorMessage(outcome.syncError, t) })}
            </p>
          ) : null}
        </div>
      ) : null}

      {decided.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('decisionSheet.raid.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {decided.map((question) => {
            const state = raidRecordState(question);
            const recordable = isRecordable(question);
            const answer = question.answer!;
            return (
              <li key={question.id} data-testid={`decision-raid-${question.code}`}>
                <label
                  className={cn(
                    'flex items-start gap-3 rounded-DEFAULT px-2 py-1.5 text-sm',
                    recordable ? 'cursor-pointer hover:bg-muted/50' : 'opacity-60',
                  )}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 shrink-0 accent-primary"
                    checked={recordable && picked.has(question.id)}
                    disabled={!recordable}
                    onChange={() => toggle(question.id)}
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="[overflow-wrap:anywhere]">
                      <span className="font-mono text-xs text-muted-foreground">
                        {question.code}
                      </span>{' '}
                      {question.title}
                    </span>
                    <span className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
                      {answer.key}. {optionTitle(question, answer.key, t('decisionSheet.other'))}
                      {answer.note.trim() ? ` — ${answer.note.trim()}` : ''}
                    </span>
                    {!recordable ? (
                      <span className="text-xs text-destructive">
                        {t('decisionSheet.raid.noDecider')}
                      </span>
                    ) : null}
                  </span>
                  {state !== 'new' ? (
                    <span
                      className={cn(
                        PILL,
                        'gap-1',
                        state === 'recorded'
                          ? 'bg-rag-green/15 text-rag-green'
                          : 'bg-rag-amber/15 text-rag-amber',
                      )}
                    >
                      {t(`decisionSheet.raid.${state}`)}
                      {raidRef(question)}
                    </span>
                  ) : null}
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {/* A text button, not the ✓ of `DialogApply`: this creates RAID items, and
          the count is the consequence the reader must see before clicking. */}
      <div className="flex justify-end">
        <Button
          disabled={chosen.length === 0 || (needsCodes && log.isPending) || record.isPending}
          onClick={() => record.mutate()}
        >
          {t('decisionSheet.raid.submit', { count: chosen.length })}
        </Button>
      </div>
    </Dialog>
  );
}
